import { ref } from 'vue';
import piniaStore from '@/pinia.store';
import useUsersStore from '@/stores/useUsersStore';
import useViewAsStore from '@/stores/useViewAsStore';
import APIResponseHandler from '@/services/APIResponseHandler';
import { TRefreshTokenResponse, TUnknownBodyParams, TParams } from './constants';
import { getAppEnvironment } from './utils';

type TPendingRequest = {
  abortController: AbortController,
  reqPromise: Promise<unknown>,
};

const ENDPOINT_REFRESH_TOKEN = '/api/token/refresh';

// single cache for all ApiClient instances
// useful for clearing via `AuthApi.ClearSession/Logout` methods
const pendingRequestsCache: Map<string, TPendingRequest> = new Map();

//

export default class ApiClient {
  isBusy = ref(false);
  private baseUrl: string;
  private onMissingTokenCallback?: () => void;
  protected userStore = useUsersStore(piniaStore);
  protected useViewAsStore = useViewAsStore(piniaStore);

  protected get pendingRequests(): Map<string, TPendingRequest> {
    return pendingRequestsCache;
  }

  // redefine in child class if needed to change basepath
  constructor() {
    const { VUE_API_BASEPATH } = getAppEnvironment();

    if (!VUE_API_BASEPATH) {
      throw new Error('The field `VUE_API_BASEPATH` is missing in the environment configuration');
    }

    this.baseUrl = VUE_API_BASEPATH;
  }

  async refreshAccessToken(): Promise<TRefreshTokenResponse> {
    const accessToken = this.userStore.accessToken;

    if (!accessToken) {
      return Promise.reject(new Error('No access token found'));
    }

    const potentiallyInProgress = this.pendingRequests.get(ENDPOINT_REFRESH_TOKEN);

    if (potentiallyInProgress) {
      return potentiallyInProgress.reqPromise as Promise<TRefreshTokenResponse>;
    }

    return this.post<TRefreshTokenResponse>(ENDPOINT_REFRESH_TOKEN, {
      body: { refresh_token: accessToken },
    })
      .then((data) => {
        this.userStore.setAccessToken(data.refresh_token);

        return data;
      })
      .catch(async () => {
        if (this.onMissingTokenCallback) {
          this.onMissingTokenCallback();
        }

        throw new Error('Failed to refresh token');
      });
  }

  fetch<T>(
    endpoint: string,
    params: TParams = {},
  ): Promise<T> {
    const url = new URL(endpoint, this.baseUrl).href;
    const potentiallyInProgress = this.pendingRequests.get(endpoint);
    const { apiProps, ...reqInit } = params;

    // protection from multi calls the same endpoint
    if (potentiallyInProgress) {
      return potentiallyInProgress.reqPromise as Promise<T>;
    }

    if (!reqInit.headers) {
      reqInit.headers = {};
    }

    const controller = new AbortController();
    const headers: Record<string, string> =
      reqInit.headers instanceof Headers
        ? Object.fromEntries(reqInit.headers.entries())
        : (reqInit.headers as Record<string, string>);

    Object.assign(reqInit, {
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    //

    this.isBusy.value = true;

    const reqPromise = fetch(url, reqInit)
      .then(response => APIResponseHandler<T>(response, {
        endpoint: url,
        reqInit,
        handlerProps: apiProps,
      }))
      .finally(() => {
        this.isBusy.value = false;
        this.pendingRequests.delete(endpoint);
      });

    this.pendingRequests.set(endpoint, {
      abortController: controller,
      reqPromise,
    });

    return reqPromise;
  }

  get<T>(endpoint: string, options?: TParams): Promise<T> {
    const reqParams = {
      ...options,
      method: 'GET',
    };

    return this.fetch<T>(endpoint, reqParams);
  }

  post<T>(endpoint: string, options?: TUnknownBodyParams): Promise<T> {
    const isFormData = options?.body instanceof FormData;

    const reqParams = {
      ...options,
      method: 'POST',
      headers: {
        ...options?.headers,
        // Only set default Content-Type for non-FormData requests
        ...(!isFormData && { 'Content-Type': 'application/json' }),
      },
      body: isFormData
        ? options.body as FormData
        : (options?.body ? JSON.stringify(options.body) : undefined),
    };

    return this.fetch<T>(endpoint, reqParams);
  }

  put<T>(endpoint: string, options?: TUnknownBodyParams): Promise<T> {
    const reqParams = {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(options?.body),
    };

    return this.fetch<T>(endpoint, reqParams);
  }

  patch<T>(endpoint: string, options?: TUnknownBodyParams): Promise<T> {
    const reqParams = {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(options?.body),
    };

    return this.fetch<T>(endpoint, reqParams);
  }

  delete<T>(endpoint: string, options?: TParams): Promise<T> {
    const reqParams = {
      ...options,
      method: 'DELETE',
    };

    return this.fetch<T>(endpoint, reqParams);
  }

  isPending(endpoint: string): boolean {
    return this.pendingRequests.has(endpoint);
  }

  abort(endpoint: string): void {
    const { abortController } = this.pendingRequests.get(endpoint) || {};

    if (abortController) {
      abortController.abort();
      this.pendingRequests.delete(endpoint);
    }
  }

  buildUrlWithParams(baseUrl: string, params: Record<string, unknown>): string {
    const urlParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => urlParams.append(key, String(item)));
        } else {
          urlParams.append(key, String(value));
        }
      }
    });

    const queryString = urlParams.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  onMissingToken(fn: () => void): void {
    this.onMissingTokenCallback = fn;
  }
}
